/**
 * Editor Image Storage System - GALLERY ONLY
 *
 * Since we migrated to S3, images are stored there with fileIds.
 * This module ONLY handles the in-memory gallery for tools with multiple results.
 * NO transient storage - use React Router with URL params instead!
 */

// In-memory storage for gallery (cleared on page refresh)
const galleryStorage = new Map<string, { toolId: string; sessionId: string; imageData: string; imageId: string; timestamp: number; index: number }>();

// ============================================================================
// SESSION GALLERY (generated images in current session)
// ============================================================================

/**
 * Add an image to the session gallery
 * Images are prepended (newest first)
 */
export async function addToGallery(
  toolId: string,
  sessionId: string,
  imageData: string,
  imageId: string
): Promise<void> {
  const key = `${toolId}-${sessionId}-${imageId}`;

  // Get current max index for this session
  const sessionKeys = Array.from(galleryStorage.keys()).filter(k => k.startsWith(`${toolId}-${sessionId}-`));
  const maxIndex = sessionKeys.length;

  galleryStorage.set(key, {
    toolId,
    sessionId,
    imageData,
    imageId,
    timestamp: Date.now(),
    index: maxIndex,
  });
}

/**
 * Get all images for a session (ordered by index, newest first)
 */
export async function getGalleryImages(
  toolId: string,
  sessionId: string
): Promise<Array<{ imageData: string; imageId: string; timestamp: number }>> {
  const allImages = Array.from(galleryStorage.values());

  const sessionImages = allImages
    .filter(item => item.toolId === toolId && item.sessionId === sessionId)
    .sort((a, b) => b.index - a.index) // Newest first
    .map(({ imageData, imageId, timestamp }) => ({ imageData, imageId, timestamp }));

  return sessionImages;
}

/**
 * Clear entire gallery for a session
 */
export async function clearGallerySession(
  toolId: string,
  sessionId: string
): Promise<void> {
  const keysToDelete = Array.from(galleryStorage.keys())
    .filter(k => k.startsWith(`${toolId}-${sessionId}-`));

  for (const key of keysToDelete) {
    galleryStorage.delete(key);
  }
}

// ============================================================================
// DEPRECATED - KEEPING FOR BACKWARD COMPAT (no-ops)
// ============================================================================

export const TRANSIENT_KEYS = { REFINE_IMAGE: 'refine-image' } as const;
export async function storeTransientImage(key: string, imageData: string, metadata?: any): Promise<void> {}
export async function getTransientImage(key: string): Promise<any> { return null; }
export async function clearTransientImage(key: string): Promise<void> {}
export async function clearAllTransientImages(): Promise<void> {}
export async function cleanupStaleTransientImages(): Promise<void> {}
